import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import {
  ROUND_COUNT_OPTIONS,
  ROUND_COUNT_MIN,
  ROUND_COUNT_MAX,
  ANSWER_TYPE_OPTIONS,
  MEDIA_ANSWER_TYPES,
  SCORING_MODEL_OPTIONS,
  DIFFICULTY_OPTIONS,
  TONE_OPTIONS,
  QUESTION_SOURCE_OPTIONS,
  defaultRoundContent,
  sampleRoundContentCsv,
  parseRoundContentCsv,
  estimateMinutes,
  makeLobbyCode,
  isContentValidForType,
  hintPenaltyTypeFor,
} from "../constants";
import { generateAi, parseAiJson } from "../../../functions/generateAi";
import { shapeForAnswerType, typeInstructionFor } from "../aiShapes";
import CountField from "./CountField";

function buildPrompt({
  brief,
  difficulty,
  tone,
  roundIndex,
  totalRounds,
  checkpointName,
  answerType,
  huntName,
  client,
  setting,
  priorQuestions,
}) {
  const settingLabel = setting ? String(setting).replace(/_/g, " / ") : "";
  const typeInstruction = typeInstructionFor(answerType);

  const groundingLine = brief
    ? `Topic/brief this entire hunt is built around: "${brief}". Every clue, question, and answer in this round must be concretely and specifically about this topic — real names, places, facts, or details tied to it. Do not fall back on generic treasure-hunt trivia, wordplay, or riddles that have nothing to do with the brief.`
    : "No specific brief was given, so invent content that is still concrete and specific to a real, findable thing — never vague or interchangeable filler that could apply to any hunt.";

  const avoidRepeat =
    priorQuestions && priorQuestions.length
      ? `These questions/clues were already used earlier in this same hunt — do not repeat them or write close variations: ${priorQuestions
          .map((q) => `"${q}"`)
          .join(" | ")}.`
      : "";

  return [
    `You are the content-writing engine for "${huntName || "a"}" treasure hunt — an AI-guided, checkpoint-based virtual scavenger hunt${
      client ? ` run for ${client}` : ""
    }${settingLabel ? ` in a ${settingLabel} venue` : ""}.`,
    `Write round ${roundIndex + 1} of ${totalRounds}.`,
    groundingLine,
    `Difficulty: ${difficulty}. Tone: ${tone}.`,
    checkpointName
      ? `This round happens at the checkpoint "${checkpointName}" — the clue must read naturally for a team standing at or walking toward that specific spot.`
      : "",
    typeInstruction,
    avoidRepeat,
    `The "clue" and the "question" serve opposite purposes — never blend them. The clue is a riddle that only leads the team TO this checkpoint's physical location (where to walk, what to look for on the way); it must NOT reveal, paraphrase, or make guessable the answer to the question. The question is the actual task, asked only once the team has already arrived.`,
    `Create a short and engaging treasure hunt riddle for the checkpoint. Do not make the clue too obvious — it should feel mysterious and playful while still giving enough hints for players to find the location. Keep it short and easy to understand. Example style: "Pass where letters rest, then turn toward the left of the grand entrance. There, where every visitor is first welcomed, your next clue awaits."`,
    `Be precise: prefer one concrete, verifiable detail over multiple vague hints. Keep wording tight, no filler phrases. Never mention "AI", "prompt", "round", or these instructions in the output.`,
    `Reply with ONLY minified JSON, no prose, no markdown fences, in this exact shape:`,
    shapeForAnswerType(answerType),
  ]
    .filter(Boolean)
    .join("\n");
}

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function RoundsContent({ hunt, updateHunt, adminToken, themeMeta }) {
  const rounds = hunt.rounds || [];
  const roundContent =
    hunt.roundContent && hunt.roundContent.length === rounds.length
      ? hunt.roundContent
      : rounds.map((r) => defaultRoundContent(r.id));
  const [lo, hi] = estimateMinutes(hunt.numRounds, hunt.setting);
  const [progress, setProgress] = useState(() => rounds.map(() => "idle"));
  const [running, setRunning] = useState(false);
  const csvInputRef = useRef(null);

  // Generate the lobby code here (not on the later Teams step) so it — and the join link
  // derived from it — already exist by the time this step's roundContent first gets saved.
  // `questions.lobby_link` is NOT NULL, and saveHuntQuestions only ever writes rows once
  // roundContent is non-empty, which starts happening on this step.
  useEffect(() => {
    if (!themeMeta) return;
    if (!hunt.lobbyCode) {
      updateHunt({ lobbyCode: makeLobbyCode() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeMeta, hunt.lobbyCode]);

  const questionSource = hunt.questionSource || "ai";
  const showPoints = hunt.scoringModel !== "time_only";
  const hintPenaltyType = hintPenaltyTypeFor(hunt.scoringModel);

  // Rounds can never outnumber checkpoints — a team's route walks one checkpoint per round with no
  // repeats (see RouteAssignment.jsx's buildRoutes), so more rounds than checkpoints exist would
  // force it to revisit one it already stood at earlier. Cap the picker itself here instead of
  // only warning after the fact, since that's the actual point rounds gets set.
  const checkpointCount = (hunt.checkpoints || []).length;
  const roundCountMax = checkpointCount > 0 ? Math.min(ROUND_COUNT_MAX, checkpointCount) : ROUND_COUNT_MAX;

  // A hunt can arrive here already over that cap — checkpoints removed after rounds was set
  // higher, or a hunt saved before this cap existed — so clamp it down instead of just blocking
  // new picks above the cap, and say so immediately (a silent clamp is easy to miss — the admin
  // would only notice rounds looked "wrong" much later, in Review or Route assignment).
  useEffect(() => {
    if (checkpointCount > 0 && hunt.numRounds > roundCountMax) {
      const requested = hunt.numRounds;
      updateHunt({ numRounds: roundCountMax });
      Swal.fire(
        "Not enough checkpoints",
        `You have ${checkpointCount} checkpoint${checkpointCount === 1 ? "" : "s"} but ${requested} rounds were set — rounds need one checkpoint each with no repeats, so this has been capped to ${roundCountMax}. Add more checkpoints on Setting & locations to raise it again.`,
        "warning"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkpointCount, roundCountMax]);

  const updateRound = (idx, patch) => {
    const next = rounds.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    updateHunt({ rounds: next });
    // Content written/generated for the old answer type doesn't necessarily fit the new one — most
    // obviously text vs mcq (no options), but even switching between two media types (e.g. GIF to
    // photo) or into qrcode leaves a `question` that reads like "capture a GIF of..." pointed at a
    // camera-photo or QR-scan round instead. isContentValidForType alone can't catch that: media/
    // qrcode types only require SOME non-empty question, so old wording silently "passes" as valid
    // for the new type without ever actually being regenerated. Clearing the round's content on any
    // real type change — not just un-approving — is what makes regeneration actually mandatory
    // instead of merely re-approval.
    if ("answerType" in patch && patch.answerType !== rounds[idx]?.answerType) {
      updateHunt({
        roundContent: roundContent.map((rc, i) => (i === idx ? defaultRoundContent(rc?.roundId ?? rounds[idx]?.id) : rc)),
      });
    }
  };

  const patchContent = (idx, patch) => {
    const next = roundContent.map((rc, i) => (i === idx ? { ...rc, ...patch, approved: false } : rc));
    updateHunt({ roundContent: next });
  };

  // Manual is for writing questions from scratch — never hand it whatever AI/CSV left behind.
  const selectQuestionSource = (val) => {
    if (val === "manual" && questionSource !== "manual") {
      updateHunt({ questionSource: val, roundContent: rounds.map((r) => defaultRoundContent(r.id)) });
    } else {
      updateHunt({ questionSource: val });
    }
  };

  const generateAll = async () => {
    if (!hunt.contentBrief.trim()) {
      Swal.fire("Add a brief", "Describe the tone/difficulty brief before generating clues.", "warning");
      return;
    }
    setRunning(true);
    setProgress(rounds.map(() => "queued"));
    const nextContent = [...roundContent];

    for (let i = 0; i < rounds.length; i++) {
      setProgress((p) => p.map((s, idx) => (idx === i ? "writing" : s)));
      try {
        const prompt = buildPrompt({
          brief: hunt.contentBrief,
          difficulty: hunt.difficulty,
          tone: hunt.tone,
          roundIndex: i,
          totalRounds: rounds.length,
          checkpointName: hunt.checkpoints?.[i % Math.max(hunt.checkpoints.length, 1)]?.name,
          answerType: rounds[i].answerType,
          huntName: hunt.huntName,
          client: hunt.client,
          setting: hunt.setting,
          priorQuestions: nextContent
            .slice(0, i)
            .map((rc) => rc?.question)
            .filter(Boolean),
        });
        const response = await generateAi({
          prompt,
          token: adminToken,
          options: { temperature: 0.5, seed: Math.floor(Math.random() * 1e9) },
        });
        const parsed = parseAiJson(response);
        const answerType = rounds[i].answerType;
        nextContent[i] = parsed
          ? {
              ...defaultRoundContent(rounds[i].id),
              clue: String(parsed.clue || "").slice(0, 200),
              question: String(parsed.question || "").slice(0, 300),
              hint: String(parsed.hint || ""),
              ...(answerType === "mcq"
                ? {
                    options: Array.isArray(parsed.options)
                      ? [0, 1, 2, 3].map((idx) => String(parsed.options[idx] || ""))
                      : ["", "", "", ""],
                    correctOption: Math.min(3, Math.max(0, parseInt(parsed.correctOption, 10) || 0)),
                    infoMessage: String(parsed.infoMessage || ""),
                  }
                : answerType === "numeric"
                ? { acceptedAnswer: String(parsed.acceptedAnswer || ""), infoMessage: String(parsed.infoMessage || "") }
                : answerType === "qrcode" || MEDIA_ANSWER_TYPES.includes(answerType)
                ? {}
                : {
                    acceptedAnswer: String(parsed.acceptedAnswer || ""),
                    alsoAccept: String(parsed.alsoAccept || ""),
                    infoMessage: String(parsed.infoMessage || ""),
                  }),
            }
          : { ...defaultRoundContent(rounds[i].id), question: String(response || "").slice(0, 300) };
        setProgress((p) => p.map((s, idx) => (idx === i ? "done" : s)));
      } catch (e) {
        setProgress((p) => p.map((s, idx) => (idx === i ? "error" : s)));
        nextContent[i] = nextContent[i] || defaultRoundContent(rounds[i].id);
      }
      updateHunt({ roundContent: [...nextContent] });
    }
    setRunning(false);
  };

  const downloadSample = () => {
    downloadTextFile(
      `${(hunt.huntName || "hunt").replace(/\s+/g, "_").toLowerCase()}_questions_sample.csv`,
      sampleRoundContentCsv(rounds.length)
    );
  };

  const handleCsvUpload = async (file) => {
    if (!file) return;
    const text = await file.text();
    const parsed = parseRoundContentCsv(text, rounds);
    if (!parsed) {
      Swal.fire("Nothing found", "That CSV didn't have any usable rows.", "warning");
      return;
    }
    updateHunt({ roundContent: parsed });
  };

  // `progress` only tracks this browser session's in-flight generation run, and resets to
  // "idle" on every mount — so on returning to this step, fall back to what's actually saved in
  // roundContent (valid content for this round's current answer type already?) instead of
  // showing "Waiting" for content that exists and was already saved.
  const statusFor = (i) => {
    if (progress[i] === "writing" || progress[i] === "error") return progress[i];
    if (isContentValidForType(roundContent[i], rounds[i]?.answerType)) return "done";
    return progress[i] || "idle";
  };
  const doneCount = rounds.filter((r, i) => statusFor(i) === "done").length;
  const allGenerated = rounds.length > 0 && doneCount === rounds.length;

  return (
    <div className="studio-step-page" data-tour="studio-step-rounds">
      <h2 className="studio-h2">Rounds &amp; content</h2>
      <p className="studio-sub">
        Set how many rounds teams play, how scoring works, and where each round's question comes from.
      </p>

      <CountField
        title="Number of rounds"
        description="Choose how many rounds teams play."
        value={hunt.numRounds}
        onChange={(n) => updateHunt({ numRounds: n })}
        options={ROUND_COUNT_OPTIONS}
        min={ROUND_COUNT_MIN}
        max={roundCountMax}
        unitLabel="rounds"
        extraHint={
          checkpointCount > 0 && checkpointCount < ROUND_COUNT_MAX
            ? `${hunt.numRounds} rounds runs about ${lo}–${hi} minutes in a ${hunt.setting.replace("_", " / ")} venue. Capped to ${checkpointCount} — one checkpoint per round, no repeats — add more checkpoints on Setting & locations to raise it.`
            : `${hunt.numRounds} rounds runs about ${lo}–${hi} minutes in a ${hunt.setting.replace("_", " / ")} venue.`
        }
      />

      <div className="studio-field">
        <label>Scoring model</label>
        <div className="studio-pill-row">
          {SCORING_MODEL_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={"studio-pill" + (hunt.scoringModel === opt.value ? " studio-pill--active" : "")}
              onClick={() => updateHunt({ scoringModel: opt.value })}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="studio-hint">Ties are broken by total time, with hint penalties added.</div>
      </div>

      <div className="studio-field">
        <label>Round format</label>
        <div className="studio-table-wrap">
          <table className="studio-table">
            <thead>
              <tr>
                <th>Round</th>
                <th>Answer type</th>
                {showPoints && <th>Points</th>}
                <th>Hint penalty ({hintPenaltyType === "points" ? "pts" : "min"})</th>
              </tr>
            </thead>
            <tbody>
              {rounds.map((r, i) => (
                <tr key={r.id}>
                  <td className="studio-table-strong">Round {i + 1}</td>
                  <td>
                    <select
                      className="studio-select"
                      value={r.answerType}
                      onChange={(e) => updateRound(i, { answerType: e.target.value })}
                    >
                      {ANSWER_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  {showPoints && (
                    <td>
                      <input
                        type="number"
                        min={1}
                        className="studio-input studio-input--num"
                        value={r.points}
                        // Same fix as hintPenaltyMin below: keep whatever's typed, including "", so
                        // the field can actually be cleared/retyped instead of snapping back to a
                        // visible 0 on every keystroke. Enforce a real value (>0) only on blur.
                        onChange={(e) => updateRound(i, { points: e.target.value })}
                        onBlur={() => {
                          const n = Number(r.points);
                          updateRound(i, { points: Number.isFinite(n) && n > 0 ? n : 1 });
                        }}
                      />
                    </td>
                  )}
                  <td>
                    <input
                      type="number"
                      className="studio-input studio-input--num"
                      value={r.hintPenaltyMin}
                      // Let the admin actually clear/retype this — coercing to a number on
                      // every keystroke (the old `Number(e.target.value) || 0`) snapped the
                      // field back to a visible 0 the instant it was emptied, making it feel
                      // impossible to clear. Keep whatever's typed, including "", and only
                      // enforce a real value (a penalty must be greater than zero — 0 or
                      // negative isn't a penalty) once they leave the field.
                      onChange={(e) => updateRound(i, { hintPenaltyMin: e.target.value })}
                      onBlur={() => {
                        const n = Number(r.hintPenaltyMin);
                        updateRound(i, { hintPenaltyMin: Number.isFinite(n) && n > 0 ? n : 1 });
                      }}
                    />{" "}
                    {hintPenaltyType === "points" ? "pts" : "min"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="studio-field">
        <label>Question source</label>
        <div className="studio-pill-row">
          {QUESTION_SOURCE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={"studio-pill" + (questionSource === opt.value ? " studio-pill--active" : "")}
              onClick={() => selectQuestionSource(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {questionSource === "ai" && (
        <div className="studio-two-col">
          <div className="studio-col-main">
            <div className="studio-field">
              <label>Brief</label>
              <textarea
                className="studio-textarea"
                rows={4}
                value={hunt.contentBrief}
                onChange={(e) => updateHunt({ contentBrief: e.target.value })}
                placeholder="Clues should reference the venue and reward observation over trivia."
              />
            </div>
            <div className="studio-field">
              <label>Difficulty</label>
              <div className="studio-pill-row">
                {DIFFICULTY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={"studio-pill" + (hunt.difficulty === opt.value ? " studio-pill--active" : "")}
                    onClick={() => updateHunt({ difficulty: opt.value })}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="studio-field">
              <label>Tone</label>
              <div className="studio-pill-row">
                {TONE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={"studio-pill" + (hunt.tone === opt.value ? " studio-pill--active" : "")}
                    onClick={() => updateHunt({ tone: opt.value })}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="studio-col-side">
            <div className="studio-side-card">
              <div className="studio-side-eyebrow">
                {running ? "Generating" : allGenerated ? "Generated" : "Generate"}
              </div>
              <ul className="studio-gen-list">
                {rounds.map((r, i) => (
                  <li key={r.id} className="studio-gen-row">
                    <span>
                      <span className={"studio-gen-dot studio-gen-dot--" + statusFor(i)} />
                      Round {i + 1}
                    </span>
                    <span className="studio-gen-status">
                      {statusFor(i) === "writing"
                        ? "Writing..."
                        : statusFor(i) === "done"
                        ? "Done"
                        : statusFor(i) === "error"
                        ? "Failed"
                        : "Waiting"}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="studio-progress-track">
                <div
                  className="studio-progress-fill"
                  style={{ width: `${rounds.length ? (doneCount / rounds.length) * 100 : 0}%` }}
                />
              </div>
              <div className="studio-hint">
                {doneCount} of {rounds.length} rounds complete
              </div>
              <button
                className="studio-btn studio-btn--primary"
                style={{ marginTop: 14, width: "100%" }}
                onClick={generateAll}
                disabled={running}
              >
                {running ? "Generating..." : allGenerated ? "Regenerate rounds" : "Generate rounds"}
              </button>
            </div>
          </div>
        </div>
      )}

      {questionSource === "csv" && (
        <div className="studio-side-card" style={{ maxWidth: 480, marginBottom: 24 }}>
          <div className="studio-side-eyebrow">Import from CSV</div>
          <p className="studio-hint" style={{ marginTop: 0 }}>
            Columns: round, clue, question, acceptedAnswer, alsoAccept, option1-4, correctOption, hint — one
            row per round. acceptedAnswer/alsoAccept only matter for text rounds; option1-4/correctOption
            (1-4) only matter for multiple-choice rounds.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="studio-btn studio-btn--ghost" onClick={downloadSample}>
              Download sample CSV
            </button>
            <button className="studio-btn studio-btn--primary" onClick={() => csvInputRef.current?.click()}>
              Upload CSV
            </button>
          </div>
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: "none" }}
            onChange={(e) => handleCsvUpload(e.target.files?.[0])}
          />
        </div>
      )}

      {questionSource === "manual" && (
        <div className="studio-field">
          <div className="studio-table-wrap">
            <table className="studio-table">
              <thead>
                <tr>
                  <th>Round</th>
                  <th>Clue</th>
                  <th>Question</th>
                  <th>Answer</th>
                  <th>Hint</th>
                </tr>
              </thead>
              <tbody>
                {rounds.map((r, i) => {
                  const rc = roundContent[i] || defaultRoundContent(r.id);
                  return (
                    <tr key={r.id}>
                      <td className="studio-table-strong">Round {i + 1}</td>
                      <td>
                        <input
                          className="studio-input"
                          value={rc.clue}
                          onChange={(e) => patchContent(i, { clue: e.target.value.slice(0, 200) })}
                          placeholder="Clue text"
                        />
                      </td>
                      <td>
                        <input
                          className="studio-input"
                          value={rc.question}
                          onChange={(e) => patchContent(i, { question: e.target.value.slice(0, 300) })}
                          placeholder="Question text"
                        />
                      </td>
                      <td>
                        {r.answerType === "mcq" && (
                          <div className="studio-mcq-options">
                            {[0, 1, 2, 3].map((optIdx) => (
                              <div className="studio-mcq-option-row" key={optIdx}>
                                <input
                                  type="radio"
                                  name={`correct-${r.id}`}
                                  checked={rc.correctOption === optIdx}
                                  onChange={() => patchContent(i, { correctOption: optIdx })}
                                  title="Mark as correct"
                                />
                                <input
                                  className="studio-input"
                                  value={rc.options?.[optIdx] || ""}
                                  onChange={(e) => {
                                    const opts = [...(rc.options || ["", "", "", ""])];
                                    opts[optIdx] = e.target.value;
                                    patchContent(i, { options: opts });
                                  }}
                                  placeholder={`Option ${optIdx + 1}`}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        {MEDIA_ANSWER_TYPES.includes(r.answerType) && (
                          <div className="studio-muted" style={{ fontSize: 13 }}>
                            Reviewed manually — no text answer needed.
                          </div>
                        )}
                        {r.answerType === "numeric" && (
                          <input
                            className="studio-input"
                            type="number"
                            value={rc.acceptedAnswer}
                            onChange={(e) => patchContent(i, { acceptedAnswer: e.target.value })}
                            placeholder="Correct number"
                          />
                        )}
                        {r.answerType === "qrcode" && (
                          <div className="studio-muted" style={{ fontSize: 13 }}>
                            Scanning any QR code at this checkpoint is the answer — no text to set here.
                          </div>
                        )}
                        {r.answerType === "text" && (
                          <>
                            <input
                              className="studio-input"
                              value={rc.acceptedAnswer}
                              onChange={(e) => patchContent(i, { acceptedAnswer: e.target.value })}
                              placeholder="Accepted answer"
                            />
                            <input
                              className="studio-input"
                              style={{ marginTop: 6 }}
                              value={rc.alsoAccept}
                              onChange={(e) => patchContent(i, { alsoAccept: e.target.value })}
                              placeholder="Also accept (comma separated)"
                            />
                          </>
                        )}
                        {["text", "numeric", "mcq"].includes(r.answerType) && (
                          <input
                            className="studio-input"
                            style={{ marginTop: 6 }}
                            value={rc.infoMessage || ""}
                            onChange={(e) => patchContent(i, { infoMessage: e.target.value })}
                            placeholder="Info message (optional context shown to the team)"
                          />
                        )}
                      </td>
                      <td>
                        <input
                          className="studio-input"
                          value={rc.hint}
                          onChange={(e) => patchContent(i, { hint: e.target.value })}
                          placeholder="Hint"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="studio-hint">These carry straight into Review content for a final approve pass.</div>
        </div>
      )}
    </div>
  );
}

export default RoundsContent;
